/**
 * Agent kit — the generic sequential step runner.
 *
 * Every agentic flow declares an ordered list of steps. The runner walks them,
 * threading an accumulating context, and emits a step record on each transition.
 * Because the list is declarative the whole plan is known before the run starts,
 * which is what lets the UI render the complete checklist up front and light
 * rows up as they land — rather than an append-only log.
 *
 * Steps are injected, so a flow's orchestration (ordering, skipping, failure
 * behaviour, trace shape) is testable without a single model call.
 */

import type {
  AgentGenerationStep,
  AgentRunTrace,
  AgentStepStatus,
} from './types';

export interface AgentStepResult<TCtx extends object> {
  /** Merged into the run context for later steps. */
  patch: Partial<TCtx>;
  /** Display/persistence summary. Keep it small — it is rendered and stored. */
  output?: Record<string, unknown>;
}

export interface AgentStep<TCtx extends object, TKey extends string> {
  key: TKey;
  /**
   * Return a reason string to skip this step, or null to run it. A skipped step
   * is reported as SKIPPED with that reason — an explicitly skipped row reads as
   * a deliberate pass, whereas a missing row reads as a failure.
   */
  shouldSkip?: (ctx: TCtx) => string | null;
  run: (ctx: TCtx) => Promise<AgentStepResult<TCtx>>;
}

export interface RunAgentOptions<TKey extends string> {
  /** Called on every transition. Drives the NDJSON stream and the UI. */
  onStep?: (step: AgentGenerationStep<TKey>) => void;
  /** Bumped with the flow's prompts/graph; recorded in the trace. */
  version: string;
  now?: () => number;
}

export interface RunAgentOutcome<TCtx extends object, TKey extends string> {
  ctx: TCtx;
  trace: AgentRunTrace<TKey>;
}

/** All steps PENDING — the plan, publishable before any work begins. */
export function initialSteps<TKey extends string>(
  keys: readonly TKey[],
): AgentGenerationStep<TKey>[] {
  return keys.map((step_key) => ({ step_key, status: 'PENDING' as AgentStepStatus }));
}

export async function runAgent<TCtx extends object, TKey extends string>(
  steps: readonly AgentStep<TCtx, TKey>[],
  initialContext: TCtx,
  options: RunAgentOptions<TKey>,
): Promise<RunAgentOutcome<TCtx, TKey>> {
  const now = options.now ?? Date.now;
  const startedAt = now();
  const keys = steps.map((step) => step.key);

  const records = new Map<TKey, AgentGenerationStep<TKey>>(
    initialSteps(keys).map((step) => [step.step_key, step]),
  );

  const emit = (step: AgentGenerationStep<TKey>) => {
    records.set(step.step_key, step);
    options.onStep?.(step);
  };

  // Publish the plan before any work begins.
  records.forEach((step) => options.onStep?.(step));

  let ctx = initialContext;

  for (const step of steps) {
    const skipReason = step.shouldSkip?.(ctx) ?? null;
    if (skipReason) {
      emit({
        step_key: step.key,
        status: 'SKIPPED',
        completed_at: new Date(now()).toISOString(),
        output: { note: skipReason },
      });
      continue;
    }

    const started_at = new Date(now()).toISOString();
    emit({ step_key: step.key, status: 'RUNNING', started_at });

    try {
      const result = await step.run(ctx);
      ctx = { ...ctx, ...result.patch };
      emit({
        step_key: step.key,
        status: 'COMPLETED',
        started_at,
        completed_at: new Date(now()).toISOString(),
        output: result.output ?? null,
      });
    } catch (error) {
      // Record where it died before propagating — for a headless run the trace
      // is the only debugging surface there will ever be.
      emit({
        step_key: step.key,
        status: 'FAILED',
        started_at,
        completed_at: new Date(now()).toISOString(),
        error_message: error instanceof Error ? error.message : 'Step failed',
      });
      throw error;
    }
  }

  return {
    ctx,
    trace: {
      steps: keys.map((key) => records.get(key)!),
      durationMs: now() - startedAt,
      version: options.version,
    },
  };
}
